import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AppState, MoneyRecordType } from '../domain/models';
import { pinValidationMessage } from '../domain/pin';
import { calculateGoalProgress, calculateRemainingWeeks, calculateTargetDate, deriveDiagnosis, formatDateKo, formatWon, sumByType } from '../domain/rules';
import { AppFrame, Card, Character, Field, Metric, PrimaryButton, ProgressBar, RoleSwitcher, SecondaryButton, StatusBanner } from '../components/ui';
import { useAppStore } from '../store/AppStore';
import { defaultHomeJourneyProfile, deriveHomePersonalization } from '../domain/homePersonalization';
import { deriveFirstWeekJourney } from '../domain/firstWeekJourney';
import { deriveWeeklyExpedition } from '../domain/weeklyExpedition';
import { useReaction } from '../reactions/ReactionProvider';
import { GuideCharacter } from '../reactions/GuideCharacter';
import { CharacterMoment, CharacterMiniBadge } from '../components/characterMoments';
import { WorldReactionStrip } from '../components/worldReactions';
import { buildFamilyCollection, buildWeeklyFamilyInsight } from '../domain/familyJourney';

const A = '/assets';
const haneul = (name: string) => `${A}/characters/haneul/haneul_${name}_v1.png`;
const sage = (name: string) => `${A}/characters/sage/sage_${name}_v1.png`;
const arcticFox = `${A}/characters/friends/arctic_fox_v1.png`;

function useReadyState() {
  const store = useAppStore();
  return store;
}

export function SplashScreen() {
  const navigate = useNavigate();
  const { login } = useReadyState();
  const openDemo = async () => {
    if (await login({ role: 'child', identifier: '하늘', secret: '1234' })) navigate('/07');
  };
  return (
    <AppFrame>
      <div className="splash-screen">
        <img src={`${A}/scenes/onboarding_snow_road_v1.png`} className="splash-scene" alt="눈길을 따라 커지는 스노우볼" />
        <div className="splash-copy">
          <p className="eyebrow">MY real Money</p>
          <h1>작은 돈 습관이<br />큰 스노우볼이 돼요</h1>
          <p>기록하고, 기다리고, 모으면서 우리 가족의 돈 이야기를 시작해요.</p>
        </div>
        <PrimaryButton onClick={() => navigate('/02')}>시작하기</PrimaryButton>
        <button className="text-link button-link" onClick={() => navigate('/demo')}>MRM 2.7 금융 패스포트·현실 탐험 데모</button>
      </div>
    </AppFrame>
  );
}

export function ParentSignupScreen() {
  const navigate = useNavigate();
  const { state, registerParent } = useReadyState();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  if (!state) return <Loading />;
  const submit = async () => {
    const ok = await registerParent({ name, email, password });
    if (ok) navigate('/consent');
  };
  return (
    <AppFrame title="부모 회원가입" backTo="/01">
      <StatusBanner />
      <div className="hero-inline">
        <div><p className="eyebrow">보호자 계정</p><h2>가족의 돈 습관을<br />함께 응원해 주세요</h2></div>
        <Character src={haneul('heart')} alt="하트를 든 하늘이" />
      </div>
      <Card>
        <Field label="이름"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="이메일"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="비밀번호"><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} autoComplete="new-password" /></Field>
        <p className="form-note">가입 후 보호자 약관·개인정보·법정대리인 동의를 별도 기록합니다.</p>
      </Card>
      <PrimaryButton onClick={() => void submit()} disabled={!name.trim() || !email.includes('@') || password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)}>가입하고 동의 확인하기</PrimaryButton>
    </AppFrame>
  );
}

export function ChildProfileScreen() {
  const navigate = useNavigate();
  const { state, updateChild, setRole, logout } = useReadyState();
  const [nickname, setNickname] = useState(state?.child.nickname ?? '하늘');
  const [birthYear, setBirthYear] = useState(state?.child.birthYear ?? 2014);
  if (!state) return <Loading />;
  const submit = async () => {
    await updateChild({ ...state.child, nickname: nickname.trim(), birthYear });
    if ((import.meta.env.VITE_API_MODE ?? 'mock') === 'http') {
      await logout();
      navigate(`/login?role=child&identifier=${encodeURIComponent(nickname.trim())}`, { replace: true });
      return;
    }
    await setRole('child');
    navigate('/04');
  };
  return (
    <AppFrame title="자녀 프로필" backTo="/02">
      <StatusBanner />
      <div className="profile-hero">
        <Character src={haneul('wave')} alt="손을 흔드는 하늘이" />
        <button className="avatar-edit" type="button" aria-label="프로필 이미지 변경 준비 중">＋</button>
      </div>
      <Card>
        <Field label="닉네임"><input value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={12} autoComplete="nickname" /></Field>
        <Field label="출생연도"><input type="number" min="2008" max="2022" value={birthYear} onChange={(e) => setBirthYear(Number(e.target.value))} inputMode="numeric" /></Field>
        <Field label="관심 목표"><div className="chip-row" aria-label="관심 목표"><span className="chip active">저축</span><span className="chip">소비</span><span className="chip">투자 기초</span></div></Field>
        <p className="form-note">자녀 로그인 PIN은 부모 홈의 계정 보안에서 별도로 설정합니다.</p>
      </Card>
      <PrimaryButton onClick={() => void submit()} disabled={!nickname.trim() || birthYear < 2008 || birthYear > 2022}>프로필 저장</PrimaryButton>
    </AppFrame>
  );
}

export function ChildPinScreen() {
  const { state, updateChildPin } = useReadyState();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saved, setSaved] = useState(false);
  if (!state) return <Loading />;
  const validation = pinValidationMessage(pin);
  const mismatch = confirmPin.length > 0 && pin !== confirmPin;
  const submit = async () => {
    setSaved(false);
    const ok = await updateChildPin({ childId: state.child.id, newPin: pin });
    if (ok) { setSaved(true); setPin(''); setConfirmPin(''); }
  };
  return <AppFrame title="자녀 PIN 관리" backTo="/parent/home" navigation="parent"><StatusBanner />
    <div className="parent-hero small"><div><p>계정 보안</p><h2>{state.child.nickname}의 간편 로그인</h2></div><Character src={sage('calculator')} alt="계정 보안을 안내하는 설옹" /></div>
    <Card>
      <Field label="새 PIN"><input type="password" inputMode="numeric" autoComplete="new-password" value={pin} onChange={(e)=>setPin(e.target.value.replace(/\D/g,'').slice(0,6))} aria-describedby="pin-help" /></Field>
      <Field label="새 PIN 확인"><input type="password" inputMode="numeric" autoComplete="new-password" value={confirmPin} onChange={(e)=>setConfirmPin(e.target.value.replace(/\D/g,'').slice(0,6))} /></Field>
      <p id="pin-help" className="form-note">숫자 4~6자리로 설정하며, 연속 숫자나 같은 숫자 반복은 피해주세요.</p>
      {pin && validation && <p className="inline-error" role="alert">{validation}</p>}
      {mismatch && <p className="inline-error" role="alert">PIN 확인 값이 일치하지 않습니다.</p>}
      {saved && <p className="inline-success" role="status">PIN이 안전하게 변경되었습니다. 기존 PIN은 더 이상 사용할 수 없습니다.</p>}
    </Card>
    <PrimaryButton onClick={() => void submit()} disabled={Boolean(validation) || mismatch || !confirmPin}>PIN 변경</PrimaryButton>
  </AppFrame>;
}

const diagnosisQuestions = [
  '용돈을 받으면 먼저 계획을 세우나요?',
  '사고 싶은 것이 생기면 하루 이상 기다릴 수 있나요?',
  '쓴 돈을 기록하거나 확인하나요?',
  '목표를 정하면 꾸준히 모으는 편인가요?',
  '가격을 비교하고 선택하나요?',
  '가족과 돈 이야기를 편하게 나누나요?'
];

export function DiagnosisScreen() {
  const navigate = useNavigate();
  const { patchState } = useReadyState();
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const answer = async (value: number) => {
    const next = [...answers, value];
    setAnswers(next);
    if (index === diagnosisQuestions.length - 1) {
      await patchState({ diagnosis: deriveDiagnosis(next) });
      navigate('/05');
    } else setIndex(index + 1);
  };
  return (
    <AppFrame title="돈 습관 진단" backTo="/03">
      <StatusBanner />
      <div className="diagnosis-progress"><span>{index + 1} / {diagnosisQuestions.length}</span><ProgressBar value={Math.round(((index + 1) / diagnosisQuestions.length) * 100)} /></div>
      <Character src={haneul('think')} alt="생각하는 하늘이" className="center-character" />
      <Card className="question-card">
        <p className="eyebrow">질문 {index + 1}</p>
        <h2>{diagnosisQuestions[index]}</h2>
        <div className="answer-grid">
          {[['전혀 아니에요', 1], ['가끔 그래요', 2], ['자주 그래요', 3], ['항상 그래요', 4]].map(([label, value]) => (
            <button key={label} onClick={() => void answer(Number(value))}>{label}</button>
          ))}
        </div>
      </Card>
    </AppFrame>
  );
}

export function DiagnosisResultScreen() {
  const navigate = useNavigate();
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const result = state.diagnosis ?? deriveDiagnosis([3, 3, 2, 3, 2, 3]);
  const copy: Record<typeof result.type, string> = {
    계획형: '목표를 정하고 순서를 세우는 힘이 있어요.',
    실천형: '생각한 것을 행동으로 옮기는 힘이 뛰어나요.',
    관찰형: '비교하고 살펴본 뒤 결정하는 힘이 있어요.',
    도전형: '새로운 습관을 시작할 성장 가능성이 커요.'
  };
  return (
    <AppFrame title="진단 결과" backTo="/04">
      <Character src={haneul('thumbs')} alt="엄지척 하늘이" className="center-character result-character" />
      <Card className="result-card">
        <p className="eyebrow">{state.child.nickname}의 머니 타입</p>
        <h2>{result.type}</h2>
        <p>{copy[result.type]}</p>
        <div className="strength-row"><span>계획력</span><span>기다림</span><span>기록력</span></div>
      </Card>
      <Card className="soft-card"><strong>첫 추천 미션</strong><p>사고 싶은 것을 하루 기다린 뒤 다시 생각해 보기</p></Card>
      <PrimaryButton onClick={() => navigate('/06')}>목표 스노우볼 만들기</PrimaryButton>
    </AppFrame>
  );
}

export function GoalScreen() {
  const navigate = useNavigate();
  const { state, saveGoal } = useReadyState();
  const [title, setTitle] = useState(state?.goal.title ?? '새로운 자전거 사기');
  const [targetAmount, setTargetAmount] = useState(state?.goal.targetAmount ?? 500000);
  const [currentAmount, setCurrentAmount] = useState(state?.goal.currentAmount ?? 50000);
  const [weeklySavingAmount, setWeeklySavingAmount] = useState(state?.goal.weeklySavingAmount ?? 10000);
  if (!state) return <Loading />;
  const goal = { ...state.goal, title, targetAmount, currentAmount, weeklySavingAmount };
  const weeks = calculateRemainingWeeks(goal);
  const targetDate = calculateTargetDate(weeks);
  const submit = async () => {
    await saveGoal({ ...goal, targetDate });
    navigate('/07');
  };
  return (
    <AppFrame title="목표 스노우볼" backTo="/05">
      <StatusBanner />
      <img className="goal-globe" src={`${A}/props/mrm_prop_03_goalsnowglobe_v02.png`} alt="목표를 상징하는 3D 스노우글로브" />
      <p className="center-copy">목표를 구체적으로 적을수록 스노우볼이 더 단단해져요.</p>
      <Card className="goal-form-card">
        <Field label="목표 이름" icon={`${A}/ui-icons/mrm_ui_02a_goalname_v01.png`}><input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="목표 금액" icon={`${A}/ui-icons/mrm_ui_02b_goalamount_v01.png`}><input type="text" inputMode="numeric" value={targetAmount.toLocaleString('ko-KR')} onChange={(e) => setTargetAmount(Number(e.target.value.replace(/\D/g, '')) || 0)} aria-label="목표 금액" /></Field>
        <Field label="현재 모은 돈" icon={`${A}/ui-icons/mrm_ui_02c_wallet_v01.png`}><input type="text" inputMode="numeric" value={currentAmount.toLocaleString('ko-KR')} onChange={(e) => setCurrentAmount(Number(e.target.value.replace(/\D/g, '')) || 0)} aria-label="현재 모은 돈" /></Field>
        <Field label="매주 모을 돈" icon={`${A}/ui-icons/mrm_ui_02d_weeklysave_v01.png`}><input type="text" inputMode="numeric" value={weeklySavingAmount.toLocaleString('ko-KR')} onChange={(e) => setWeeklySavingAmount(Number(e.target.value.replace(/\D/g, '')) || 0)} aria-label="매주 모을 돈" /></Field>
      </Card>
      <Card className="estimate-card">
        <div><span>예상 달성</span><strong>{weeks}주</strong><small>{formatDateKo(targetDate)}</small></div>
        <Character src={haneul('star')} alt="별을 든 하늘이" />
      </Card>
      <PrimaryButton onClick={() => void submit()} disabled={!title.trim() || targetAmount <= 0 || currentAmount < 0 || currentAmount > targetAmount || weeklySavingAmount <= 0}>목표 만들기</PrimaryButton>
    </AppFrame>
  );
}

export function ChildHomeScreen() {
  const { state, patchState } = useReadyState();
  if (!state) return <Loading />;
  return <ChildHomeReady state={state} patchState={patchState} />;
}

function ChildHomeReady({ state, patchState }: { state: AppState; patchState: (patch: Partial<AppState>) => Promise<void> }) {
  const navigate = useNavigate();
  const mission = state.missions[0];
  const journeyProfile = state.homeJourney ?? defaultHomeJourneyProfile(state.child.id);
  const personalization = deriveHomePersonalization({
    childId: state.child.id,
    profile: journeyProfile,
    records: state.records,
    mission,
    quizAttempts: state.quizAttempts,
  });
  const primaryAction = personalization.dailyDoneCount === 3
    ? { label:'오늘의 눈길을 완성했어!', reason:'오늘 할 일을 모두 마쳤어요. 스노우 월드에서 달라진 풍경을 확인해 보세요.', to:'/child/world', button:'월드 둘러보기' }
    : { label:personalization.nextAction.label, reason:personalization.reason, to:personalization.nextAction.to, button:'시작하기' };
  const continueAction = mission?.status === 'submitted'
    ? { label:'부모님 확인을 기다리는 미션이 있어요', to:'/child/missions', cta:'상태 보기' }
    : personalization.nextAction.to !== '/child/missions'
      ? { label:'오늘의 미션도 이어서 할 수 있어요', to:'/child/missions', cta:'이어서' }
      : { label:'첫 7일 성장 루트를 살펴봐요', to:'/child/first-week', cta:'보기' };

  const openPrimary = async () => {
    if (personalization.phase === 'welcome' && !journeyProfile.welcomeCompletedAt) {
      const updatedAt = new Date().toISOString();
      await patchState({ homeJourney: { ...journeyProfile, childId:state.child.id, welcomeCompletedAt:updatedAt, lastPhase:'restart', lastActionKey:personalization.nextAction.key, lastSeenDate:personalization.dateKey, updatedAt } });
    }
    navigate(primaryAction.to);
  };

  return <AppFrame navigation="child" screenClass="child-home-simple-2-8">
    <StatusBanner />
    <section className="simple-child-greeting-2-8">
      <div><small>오늘의 베이스캠프</small><h1>{state.child.nickname}아,<br/>{personalization.dailyDoneCount === 3 ? '오늘도 잘 해냈어!' : '하나부터 시작해 볼까?'}</h1></div>
      <GuideCharacter guide="snows" pose="guide" state={personalization.dailyDoneCount === 3 ? 'celebrate' : 'idle'} alt="오늘의 행동을 안내하는 스노스" priority />
    </section>
    <section className="simple-primary-action-2-8" aria-label="오늘의 주요 행동">
      <span>오늘의 미션 · 약 2분</span>
      <h2>{primaryAction.label}</h2>
      <p>{primaryAction.reason}</p>
      <button type="button" data-reaction={personalization.dailyDoneCount === 3 ? 'day.completed' : 'mission.start'} onClick={() => void openPrimary()}>{primaryAction.button}<b>›</b></button>
    </section>
    <Link className="simple-continue-action-2-8" data-reaction="choice.selected" to={continueAction.to}><span><small>이어서 할 일</small><strong>{continueAction.label}</strong></span><b>{continueAction.cta} ›</b></Link>
    <p className="simple-home-hint-2-8">다른 기능은 왼쪽 위 <strong>전체 메뉴</strong> 또는 아래 메뉴에서 찾을 수 있어요.</p>
  </AppFrame>;
}

export function MoneyRecordScreen() {
  const navigate = useNavigate();
  const { emit } = useReaction();
  const { state, addRecord } = useReadyState();
  const [type, setType] = useState<MoneyRecordType>('saving');
  const [amount, setAmount] = useState(10000);
  const [category, setCategory] = useState('저축');
  const [mood, setMood] = useState('좋아요');
  const [memo, setMemo] = useState('');
  if (!state) return <Loading />;
  const submit = async () => {
    await addRecord({ id: `rec_${Date.now()}`, childId: state.child.id, type, amount, category, mood, memo, occurredAt: new Date().toISOString() });
    await emit('record.saved');
    navigate('/07');
  };
  return (
    <AppFrame title="용돈 기록" navigation="child">
      <StatusBanner />
      <div className="record-character"><Character src={haneul('report')} alt="기록하는 하늘이" /></div>
      <Card>
        <div className="record-type-row">
          {([['income', '받았어요', 'mrm_ui_03a_received_v01.png'], ['expense', '썼어요', 'mrm_ui_03b_spent_v01.png'], ['saving', '모았어요', 'mrm_ui_03c_saved_v01.png']] as const).map(([value, label, icon]) => (
            <button key={value} data-reaction="choice.selected" className={type === value ? 'active' : ''} onClick={() => setType(value)}><img src={`${A}/ui-icons/${icon}`} alt="" />{label}</button>
          ))}
        </div>
        <Field label="금액"><input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} /></Field>
        <Field label="카테고리"><select value={category} onChange={(e) => setCategory(e.target.value)}><option>저축</option><option>간식</option><option>문구</option><option>장난감</option><option>기타</option></select></Field>
        <Field label="기분"><select value={mood} onChange={(e) => setMood(e.target.value)}><option>좋아요</option><option>보통</option><option>아쉬워요</option><option>속상해요</option></select></Field>
        <Field label="메모"><textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="무엇을 위해 기록했나요?" /></Field>
      </Card>
      <PrimaryButton onClick={() => void submit()} disabled={amount <= 0}>기록 저장</PrimaryButton>
    </AppFrame>
  );
}

export function MissionScreen() {
  const { state, updateMission } = useReadyState();
  const { emit } = useReaction();
  if (!state) return <Loading />;
  const mission = state.missions[0];
  const submit = async () => {
    await updateMission({ ...mission, status: 'submitted', submittedAt: new Date().toISOString() });
    await emit('mission.completed');
  };
  return (
    <AppFrame title="오늘의 미션" navigation="child">
      <StatusBanner />
      <Character src={haneul('run')} alt="달리는 하늘이" className="center-character mission-character" />
      <Card className="mission-main-card">
        <p className="eyebrow">기다림 미션</p><h2>{mission.title}</h2><p>{mission.description}</p>
        <div className="mission-meta"><span>약 3분</span><span>보상 {formatWon(mission.rewardAmount)}</span></div>
      </Card>
      <Card className="soft-card"><strong>오늘의 팁</strong><p>메모장에 사고 싶은 이유를 적어보면 내 마음을 더 잘 알 수 있어요.</p></Card>
      {mission.status === 'submitted' ? <div className="success-panel">부모님께 승인 요청을 보냈어요.</div> : <PrimaryButton onClick={() => void submit()}>미션 완료하고 승인 요청</PrimaryButton>}
      <Link className="feature-card family-reward-link" to="/child/family-missions"><strong>가족 미션·상금형 용돈</strong><span>가족이 정한 실천 약속과 보상 장부를 확인해요</span><b>›</b></Link>
    </AppFrame>
  );
}

export function ParentHomeScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const pendingMissions = state.missions.filter((mission) => mission.status === 'submitted').length;
  const pendingPassport = state.passportVisits.filter((visit) => visit.status === 'submitted').length;
  const hasPending = pendingMissions + pendingPassport > 0;
  const mainAction = hasPending
    ? { eyebrow:'지금 확인할 일', title:`확인 대기 ${pendingMissions + pendingPassport}건`, copy:'아이의 과정 기록을 확인하고 짧은 응원을 보내 주세요.', to: pendingMissions > 0 ? '/parent/approvals' : '/parent/passport', button:'확인하고 응원하기' }
    : { eyebrow:'이번 주 성장', title:'계획하는 힘이 자라고 있어요', copy:'이번 주의 선택을 점수보다 성장 이야기로 살펴보세요.', to:'/parent/weekly-insight', button:'성장 이야기 보기' };
  return <AppFrame navigation="parent" screenClass="parent-home-simple-2-8">
    <StatusBanner />
    <section className="simple-parent-heading-2-8"><div><small>{state.child.nickname}의 성장 파트너</small><h1>지금 필요한<br/>응원부터</h1></div><GuideCharacter guide="sage" pose="checklist" state="quiet" alt="부모의 확인을 돕는 설옹" priority /></section>
    <section className="simple-parent-action-2-8">
      <span>{mainAction.eyebrow}</span><h2>{mainAction.title}</h2><p>{mainAction.copy}</p><Link to={mainAction.to} data-reaction="choice.selected">{mainAction.button}<b>›</b></Link>
    </section>
    <Link className="simple-parent-summary-2-8" to="/parent/weekly-insight"><span><small>이번 주 한 줄</small><strong>기록 {state.records.length}회 · 승인 미션 {state.missions.filter((mission) => mission.status === 'approved').length}회</strong></span><b>성장 보기 ›</b></Link>
    <Link className="simple-parent-family-2-8" data-reaction="family.cheer" to="/parent/family-expedition"><span><small>가족과 함께</small><strong>이번 주 공동 미션을 골라 보세요</strong></span><b>제안하기 ›</b></Link>
    <p className="simple-home-hint-2-8">보상·기록·설정은 왼쪽 위 <strong>전체 메뉴</strong>에서 확인할 수 있어요.</p>
  </AppFrame>;
}

export function MissionApprovalScreen() {
  const navigate = useNavigate();
  const { emit } = useReaction();
  const { state, updateMission } = useReadyState();
  if (!state) return <Loading />;
  const mission = state.missions[0];
  const decide = async (status: 'approved' | 'held') => {
    await updateMission({ ...mission, status, approvedAt: new Date().toISOString() });
    if (status === 'approved') await emit('family.cheer');
    navigate('/10');
  };
  return (
    <AppFrame title="미션 승인" backTo="/parent/home" navigation="parent">
      <StatusBanner />
      <Character src={sage('checklist')} alt="승인을 돕는 설옹" className="center-character compact-character" />
      <Card>
        <p className="eyebrow">{state.child.nickname}의 승인 요청</p><h2>{mission.title}</h2><p>“오늘은 바로 사지 않고 내일 다시 생각해 보기로 했어요.”</p>
        <div className="approval-status">현재 상태: <strong>{mission.status}</strong></div>
      </Card>
      <Card className="soft-card"><strong>추천 응원 문구</strong><p>기다린 선택이 정말 멋지구나. 내일의 마음도 함께 살펴보자.</p></Card>
      <div className="button-row"><SecondaryButton onClick={() => void decide('held')}>보류</SecondaryButton><PrimaryButton onClick={() => void decide('approved')}>승인하기</PrimaryButton></div>
      <Link className="feature-card" to="/parent/passport"><strong>금융 패스포트 승인</strong><span>체크 항목과 한 줄 기록을 확인하고 현실 탐험 스탬프를 승인해요</span><b>›</b></Link>
    </AppFrame>
  );
}

export function RewardSettingScreen() {
  const navigate = useNavigate();
  const { state, updateRewardRule } = useReadyState();
  const [rule, setRule] = useState(state?.rewardRule);
  if (!state || !rule) return <Loading />;
  const toggle = (key: keyof typeof rule) => setRule({ ...rule, [key]: !rule[key] });
  const save = async () => { await updateRewardRule(rule); navigate('/10'); };
  return (
    <AppFrame title="보상 설정" backTo="/parent/home" navigation="parent">
      <StatusBanner />
      <div className="parent-hero small"><div><p>현금보다 습관을 먼저</p><h2>가족의 보상 규칙</h2></div><Character src={sage('calculator')} alt="계산기를 든 설옹" /></div>
      <CharacterMoment src={haneul('piggy')} alt="저금통을 안은 하늘이" eyebrow="보상 설계 원칙" title="돈의 크기보다 행동의 의미를 보상해요" copy="기록, 기다림, 가족 대화처럼 반복 가능한 습관에 보상을 연결합니다." tone="gold" compact />
      <Card className="settings-list">
        <label><span>연속 실천 보상</span><input type="checkbox" checked={rule.streakEnabled} onChange={() => toggle('streakEnabled')} /></label>
        <label><span>목표 달성 보상</span><input type="checkbox" checked={rule.goalEnabled} onChange={() => toggle('goalEnabled')} /></label>
        <label><span>가족 대화 보상</span><input type="checkbox" checked={rule.familyTalkEnabled} onChange={() => toggle('familyTalkEnabled')} /></label>
        <label><span>부모 매칭 저축</span><input type="checkbox" checked={rule.parentMatchEnabled} onChange={() => toggle('parentMatchEnabled')} /></label>
        <Field label="목표 달성 보상 금액"><input type="number" value={rule.goalAmount} onChange={(e) => setRule({ ...rule, goalAmount: Number(e.target.value) })} /></Field>
      </Card>
      <PrimaryButton onClick={() => void save()}>보상 규칙 저장</PrimaryButton>
      <Link className="feature-card family-reward-link" to="/parent/family-missions"><strong>가족 미션·용돈 장부 열기</strong><span>실제 지급 전 승인과 기록을 분리해서 관리해요</span><b>›</b></Link>
    </AppFrame>
  );
}

export function WeeklyReportScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const saving = sumByType(state.records, 'saving');
  const expense = sumByType(state.records, 'expense');
  return (
    <AppFrame title="주간 리포트" backTo="/parent/home" navigation="parent">
      <div className="report-hero"><div><p>이번 주 성장 관찰</p><h2>{state.child.nickname}의 돈 습관</h2></div><Character src={sage('binocular')} alt="쌍안경을 든 설옹" /></div>
      <CharacterMoment src={arcticFox} alt="성장 단서를 찾는 북극여우" eyebrow="북극여우가 찾은 단서" title="꾸준한 기록이 선택의 이유를 선명하게 만들었어요" copy="횟수뿐 아니라 기록 속 감정과 이유가 어떻게 달라졌는지 함께 살펴보세요." tone="violet" compact />
      <div className="metric-grid three"><Metric label="저축" value={formatWon(saving)} /><Metric label="소비" value={formatWon(expense)} /><Metric label="미션" value={`${state.missions.filter((m) => m.status === 'approved').length}회`} /></div>
      <Card><h3>성장 신호</h3><div className="bar-list"><div><span>기다림</span><ProgressBar value={78} /></div><div><span>기록</span><ProgressBar value={86} /></div><div><span>계획</span><ProgressBar value={68} /></div></div></Card>
      <Card className="soft-card"><strong>부모 코칭 제안</strong><p>결과보다 “왜 그렇게 선택했는지”를 먼저 물어보세요.</p></Card>
      <Link className="feature-card economy-home-card" to="/parent/reports/economy"><strong>경제기사 학습 인사이트</strong><span>기사 참여·이해·가족 대화 질문을 확인해요</span><b>›</b></Link>
      <Link className="feature-card growth-home-card" to="/parent/reports/growth"><strong>30일 성장·냥장고 인사이트</strong><span>연속 실천과 활동 균형을 확인해요</span><b>›</b></Link>
      <Link className="feature-card family-insight-link-2-6" to="/parent/weekly-insight"><strong>2.6 가족 성장 인사이트</strong><span>다섯 가지 성장 힘·응원·공동 미션을 한 흐름으로 확인해요</span><b>›</b></Link>
    </AppFrame>
  );
}

export function FamilyTalkScreen() {
  const navigate = useNavigate();
  const { emit } = useReaction();
  return (
    <AppFrame title="가족 대화 카드" backTo="/child/home" navigation="child">
      <img className="talk-prop" src={`${A}/props/mrm_prop_05_familytalkneedswants_v01.png`} alt="필요와 원하는 것을 구분하는 대화 카드" />
      <Card className="talk-card"><p className="eyebrow">오늘의 질문</p><h2>필요한 것과 원하는 것은 어떻게 다를까요?</h2><p>가족 각자가 최근 사고 싶었던 것을 하나씩 말하고, 왜 필요한지 또는 원하는지 이야기해 보세요.</p></Card>
      <Character src={haneul('heart')} alt="하트를 든 하늘이" className="center-character compact-character" />
      <CharacterMiniBadge src={sage('checklist')} alt="가족 대화 질문을 준비한 설옹" label="설옹의 대화 팁" detail="정답을 알려주기보다 아이의 이유를 끝까지 들어주세요." />
      <PrimaryButton onClick={() => { void emit('family.cheer'); navigate('/15'); }}>대화하고 응원 남기기</PrimaryButton>
    </AppFrame>
  );
}

export function ParentMessageScreen() {
  const navigate = useNavigate();
  const { emit } = useReaction();
  const { state, sendEncouragement } = useReadyState();
  const [body, setBody] = useState('하늘아, 기다림은 정말 멋진 힘이야! 오늘도 잘해줘서 고마워.');
  if (!state) return <Loading />;
  const send = async () => {
    await sendEncouragement({ id: `msg_${Date.now()}`, familyId: state.session.familyId, senderName: state.user.name, body, createdAt: new Date().toISOString() });
    await emit('family.cheer');
    navigate('/10');
  };
  return (
    <AppFrame title="부모 응원 메시지" backTo="/parent/home" navigation="parent">
      <Character src={haneul('heart')} alt="응원하는 하늘이" className="center-character" />
      <CharacterMiniBadge src={sage('checklist')} alt="응원 문장을 안내하는 설옹" label="응원 문장 공식" detail="행동을 관찰하고, 의미를 인정하고, 다음 선택을 믿어주세요." />
      <Card>
        <Field label="응원 한마디"><textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={120} /></Field>
        <small className="counter">{body.length}/120</small>
      </Card>
      <div className="chip-row"><button className="chip" onClick={() => setBody('기다린 선택이 정말 멋지구나!')}>기다림 칭찬</button><button className="chip" onClick={() => setBody('꾸준히 기록한 네가 자랑스러워!')}>기록 칭찬</button></div>
      <PrimaryButton onClick={() => void send()} disabled={!body.trim()}>응원 보내기</PrimaryButton>
    </AppFrame>
  );
}

export function BadgeLevelScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const badges = [
    ['첫 기록', 'mrm_badge_11_firstrecord_v01.png'],
    ['기다림 성공', 'mrm_badge_12_waitingsuccess_v01.png'],
    ['가격 비교', 'mrm_badge_13_pricecompare_v01.png'],
    ['목표 50%', 'mrm_badge_14_goalhalf_v01.png'],
    ['꾸준한 실천', 'mrm_badge_15_steadypractice_locked_v01.png']
  ];
  return (
    <AppFrame title="배지와 레벨" navigation="child">
      <div className="level-hero"><div><p>현재 레벨</p><h2>Lv.{state.level} 성장하는 저축가</h2><ProgressBar value={state.xp % 100} /></div><Character src={haneul('jump')} alt="점프하는 하늘이" /></div>
      <CharacterMoment src={arcticFox} alt="배지 컬렉션을 살펴보는 북극여우" eyebrow="배지 컬렉션 가이드" title="배지는 돈의 크기가 아니라 선택의 깊이를 기억해요" copy="첫 기록, 기다림, 비교, 꾸준함처럼 내가 키운 힘을 하나씩 모아 보세요." tone="ice" compact />
      <div className="badge-grid">{badges.map(([label, file]) => <Card key={label}><img src={`${A}/badges/${file}`} alt={`${label} 배지`} /><strong>{label}</strong></Card>)}</div>
      <div className="badge-screen-actions"><Link className="primary-button link-button" to="/child/hall-of-honor">수료증·배지 명예의 전당</Link><Link className="secondary-button" to="/17">30일 성장 리포트</Link></div>
    </AppFrame>
  );
}

export function GrowthReportScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const progress = calculateGoalProgress(state.goal);
  return (
    <AppFrame title="30일 성장 리포트" backTo="/child/badges" navigation="child">
      <div className="report-hero"><div><p>30일간의 변화</p><h2>{state.child.nickname}의 스노우볼</h2></div><Character src={haneul('report')} alt="리포트 하늘이" /></div>
      <Card className="circle-score"><div style={{ '--score': `${progress * 3.6}deg` } as CSSProperties}><strong>{progress}%</strong><span>목표 달성</span></div><p>기록 {state.records.length}건 · 미션 {state.missions.length}개 · 배지 {state.badges.length}개</p></Card>
      <div className="metric-grid"><Metric label="모은 돈" value={formatWon(state.goal.currentAmount)} /><Metric label="현재 레벨" value={`Lv.${state.level}`} /></div>
      <CharacterMoment src={sage('binocular')} alt="30일 성장 기록을 해석하는 설옹" eyebrow="설옹의 성장 해석" title="가장 크게 자란 힘은 ‘한 번 더 생각하는 힘’이에요" copy="기록과 기다림이 반복되면서 충동보다 목표를 먼저 떠올리는 선택이 늘었습니다." tone="navy" compact />
      <Card><h3>가장 크게 자란 힘</h3><p>사고 싶은 마음을 바로 행동으로 옮기지 않고 한 번 더 생각하는 힘이 자랐어요.</p></Card>
      <Link className="primary-button link-button" to="/child/hall-of-honor">수료증·배지 컬렉션 보기</Link>
    </AppFrame>
  );
}

export function CertificateScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const progress = calculateGoalProgress(state.goal);
  const approvedMissions = state.missions.filter((mission) => mission.status === 'approved').length;
  const issueDate = new Date().toISOString().slice(0, 10);
  const certificateId = `MRM-${new Date().getFullYear()}-${state.child.id.slice(-3).toUpperCase()}`;
  const achievements = [
    { label: '돈 기록', value: `${state.records.length}회`, icon: `${A}/ui-icons/mrm_ui_01a_record_v01.png` },
    { label: '미션 완주', value: `${approvedMissions}회`, icon: `${A}/ui-icons/mrm_ui_12a_mission_v01.png` },
    { label: '받은 배지', value: `${state.badges.length}개`, icon: `${A}/badges/mrm_badge_02_missionrewardstar_v01.png` },
    { label: '목표 성장', value: `${progress}%`, icon: `${A}/ui-icons/mrm_ui_12c_saving_v01.png` },
  ];
  return (
    <AppFrame title="수료증" backTo="/child/me" navigation="child" screenClass="certificate-screen">
      <section className="certificate-stage">
        <article className="certificate-sheet" role="document" aria-label={`${state.child.nickname}의 MY real Money 공식 성장 수료증`}>
          <span className="certificate-corner corner-a" aria-hidden="true" /><span className="certificate-corner corner-b" aria-hidden="true" /><span className="certificate-corner corner-c" aria-hidden="true" /><span className="certificate-corner corner-d" aria-hidden="true" />
          <header className="certificate-official-header">
            <div className="certificate-brand-lockup"><span>MY</span><div><strong>MY real Money</strong><small>SNOWBALL FINANCIAL GROWTH PROGRAM</small></div></div>
            <img className="certificate-medal" src={`${A}/certificates/mrm_cert_02_goldmedal_v01.png`} alt="공식 수료 금메달" />
            <div className="certificate-authority"><small>OFFICIAL CERTIFICATE</small><strong>공식 성장 인증</strong></div>
          </header>

          <section className="certificate-title-block">
            <p>30일 돈 습관 탐험을 완주한 어린이에게 수여합니다</p>
            <h1>스노우볼 금융 성장 수료증</h1>
            <span>Certificate of Snowball Financial Growth</span>
          </section>

          <section className="certificate-recipient">
            <small>수료자</small>
            <h2>{state.child.nickname}</h2>
            <i aria-hidden="true" />
            <p>기록하고, 기다리고, 목표를 향해 꾸준히 선택하며<br className="desktop-only" /> 자신만의 건강한 돈 습관 스노우볼을 만들었습니다.</p>
          </section>

          <section className="certificate-achievement-grid" aria-label="수료 성취 요약">
            {achievements.map((item) => <div key={item.label}><img src={item.icon} alt="" /><span>{item.label}</span><strong>{item.value}</strong></div>)}
          </section>

          <section className="certificate-canon-quote">
            <span>MRM 성장 원칙</span>
            <blockquote>“돈의 크기보다 선택의 깊이가 스노우볼을 키웁니다.”</blockquote>
          </section>

          <footer className="certificate-footer">
            <div className="certificate-character certificate-character-haneul"><Character src={haneul('celebrate')} alt="수료를 축하하는 하늘이" /></div>
            <div className="certificate-issue-info"><small>발급번호</small><strong>{certificateId}</strong><span>{formatDateKo(issueDate)}</span></div>
            <div className="certificate-signature"><small>MY real Money 성장교육 프로그램</small><strong>NEXPA.AI</strong><span>공식 인증</span></div>
            <img className="certificate-seal" src={`${A}/certificates/mrm_cert_03_completionseal_v01.png`} alt="MY real Money 공식 수료 인증 도장" />
            <div className="certificate-character certificate-character-sage"><Character src={sage('checklist')} alt="수료 기록을 인증하는 설옹" /></div>
          </footer>
        </article>
      </section>
      <Card className="certificate-keep-note"><strong>연도 냥장고에 보관해요</strong><p>수료증은 점수표가 아니라 내가 어떤 선택을 반복했는지 기억하는 성장 기록입니다.</p></Card>
      <div className="certificate-actions"><PrimaryButton onClick={() => window.print()}>인쇄·PDF로 저장</PrimaryButton><Link className="secondary-button" to="/17">성장 리포트 다시 보기</Link></div>
    </AppFrame>
  );
}


export function ConceptLearningScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const concepts = [
    { title: '필요와 원하는 것', note: '지금 꼭 필요한지, 조금 기다려도 되는지 구분해요.', icon: 'mrm_ui_08_familytalk_v01.png', progress: 80 },
    { title: '충동 조절', note: '사고 싶은 마음이 생겼을 때 한 번 더 생각해요.', icon: 'mrm_ui_06_impulsecontrol_v01.png', progress: 65 },
    { title: '목표와 기다림', note: '작은 저축이 목표에 가까워지는 과정을 이해해요.', icon: 'mrm_ui_07_missiontip_v01.png', progress: 55 },
    { title: '기록과 비교', note: '받고, 쓰고, 모은 기록으로 내 습관을 발견해요.', icon: 'mrm_ui_09_report_v01.png', progress: 40 }
  ];
  const todayArticle = state.economicArticles.find((article) => article.status === 'published');
  const completed = state.quizAttempts.filter((attempt) => attempt.childId === state.child.id).length;
  return (
    <AppFrame title="개념 학습" navigation="child" screenClass="concept-screen">
      <div className="hero-inline"><div><p className="eyebrow">경제 개념 도서관</p><h2>오늘 배운 개념을<br />나의 선택에 연결해요</h2></div><Character src={sage('research')} alt="경제 개념을 안내하는 설옹" /></div>
      {todayArticle && <Link className="economy-feature-module" to={`/child/concepts/economy/${todayArticle.id}`}><div><span>오늘의 경제기사 · {todayArticle.readingMinutes}분</span><h2>{todayArticle.title}</h2><p>{todayArticle.childSummary}</p><strong>퀴즈 완료 {completed}회 · 기사 읽기 ›</strong></div><Character src={haneul('star')} alt="별을 든 하늘이" /></Link>}
      <div className="module-card-grid">
        {concepts.map((concept) => <Card key={concept.title} className="concept-module-card"><img src={`${A}/ui-icons/${concept.icon}`} alt="" /><div><h3>{concept.title}</h3><p>{concept.note}</p><ProgressBar value={concept.progress} /></div></Card>)}
      </div>
      <Card className="soft-card"><strong>오늘의 개념 미션</strong><p>최근 사고 싶었던 물건 하나를 떠올리고 ‘필요한 것’인지 ‘원하는 것’인지 말해 보세요.</p></Card>
    </AppFrame>
  );
}

export function ChildMeScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  return (
    <AppFrame title="나의 성장" navigation="child" screenClass="child-me-screen">
      <div className="level-hero"><div><p>{state.child.nickname}의 현재 레벨</p><h2>Lv.{state.level} 성장하는 저축가</h2><ProgressBar value={state.xp % 100} /></div><Character src={haneul('thumbs')} alt="엄지손가락을 든 하늘이" /></div>
      <div className="metric-grid three"><Metric label="기록" value={`${state.records.length}건`} /><Metric label="배지" value={`${state.badges.length}개`} /><Metric label="경험치" value={`${state.xp} XP`} /></div>
      <div className="module-card-grid two-column">
        <Link className="feature-card" to="/child/growth-board"><strong>30일 성장보드</strong><span>오늘의 실천 칸을 열고 연속 기록을 이어가요</span><b>›</b></Link>
        <Link className="feature-card" to="/child/fridge"><strong>연간 냥장고</strong><span>한 해 동안 모은 성장 아이템을 확인해요</span><b>›</b></Link>
        <Link className="feature-card" to="/child/passport"><strong>금융 패스포트</strong><span>생활 속 경제를 발견하고 가족 확인으로 탐험 스탬프를 모아요</span><b>›</b></Link>
        <Link className="feature-card" to="/child/badges"><strong>배지 컬렉션</strong><span>획득한 성취와 다음 도전을 확인해요</span><b>›</b></Link>
        <Link className="feature-card" to="/17"><strong>30일 성장 리포트</strong><span>나의 기록에서 자란 습관을 확인해요</span><b>›</b></Link>
        <Link className="feature-card" to="/child/hall-of-honor"><strong>스노우볼 명예의 전당</strong><span>미션 수료증과 배지를 모아 성장 기록을 전시해요</span><b>›</b></Link>
        <Link className="feature-card" to="/14"><strong>가족 대화 카드</strong><span>부모님과 돈 이야기를 나눠요</span><b>›</b></Link>
      </div>
    </AppFrame>
  );
}

export function ParentRecordsScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  const income = sumByType(state.records, 'income');
  const expense = sumByType(state.records, 'expense');
  const saving = sumByType(state.records, 'saving');
  return (
    <AppFrame title="기록" navigation="parent" screenClass="parent-records-screen">
      <div className="report-hero"><div><p>자녀 기록 모니터링</p><h2>{state.child.nickname}의 돈 흐름</h2></div><Character src={sage('binocular')} alt="기록을 관찰하는 설옹" /></div>
      <div className="metric-grid three"><Metric label="받은 돈" value={formatWon(income)} /><Metric label="쓴 돈" value={formatWon(expense)} /><Metric label="모은 돈" value={formatWon(saving)} /></div>
      <Card><div className="card-heading"><div><span>최근 활동</span><h2>용돈·소비·저축 기록</h2></div><strong>{state.records.length}건</strong></div>
        <div className="record-list">{state.records.slice().reverse().map((record) => <article key={record.id}><span className={`record-type-dot ${record.type}`} /><div><strong>{record.category}</strong><small>{record.memo || record.mood} · {formatDateKo(record.occurredAt.slice(0,10))}</small></div><b>{record.type === 'expense' ? '-' : '+'}{formatWon(record.amount)}</b></article>)}</div>
      </Card>
      <Link className="primary-button link-button" to="/parent/reports">주간 리포트로 분석하기</Link>
    </AppFrame>
  );
}

export function ParentFamilyTalkDashboardScreen() {
  const { state } = useReadyState();
  if (!state) return <Loading />;
  return (
    <AppFrame title="가족 대화" navigation="parent" screenClass="parent-talk-screen">
      <div className="parent-hero"><div><p>질문하고, 듣고, 응원해요</p><h2>돈 이야기가<br />가족의 성장 대화가 돼요</h2></div><Character src={sage('checklist')} alt="가족 대화를 안내하는 설옹" /></div>
      <Card className="talk-card"><p className="eyebrow">이번 주 추천 질문</p><h2>필요한 것과 원하는 것은 어떻게 다를까요?</h2><p>결론을 먼저 알려주기보다 아이가 선택의 이유를 설명하도록 기다려 주세요.</p><Link className="secondary-button link-button" to="/14">대화 카드 미리 보기</Link></Card>
      <Card><div className="card-heading"><div><span>최근 응원</span><h2>가족 응원 기록</h2></div><strong>{state.messages.length}건</strong></div>
        <div className="message-history">{state.messages.map((message) => <article key={message.id}><strong>{message.senderName}</strong><p>{message.body}</p><small>{formatDateKo(message.createdAt.slice(0,10))}</small></article>)}</div>
      </Card>
      <Link className="primary-button link-button" to="/15">새 응원 메시지 보내기</Link>
    </AppFrame>
  );
}

export function AdminScreen() {
  const { state, reset } = useReadyState();
  if (!state) return <Loading />;
  return (
    <AppFrame title="운영 관리자">
      <RoleSwitcher />
      <Card><h2>운영 상태</h2><div className="metric-grid"><Metric label="가족" value="1" /><Metric label="자녀" value="1" /><Metric label="승인 대기" value={state.missions.filter((m) => m.status === 'submitted').length} /></div></Card>
      <Card><h3>미션 템플릿</h3><p>기다림 미션 · 기록 미션 · 가격 비교 미션</p></Card>
      <Card><h3>환경</h3><p>API 모드: MOCK / 개인정보 데이터: 샘플 전용</p></Card>
      <SecondaryButton onClick={() => void reset()}>데모 데이터 초기화</SecondaryButton>
    </AppFrame>
  );
}

export function OperationsIndexScreen() {
  const groups = [
    { id: 'start', label: '시작', icon: '🚀', items: [['01', '스플래시'], ['02', '부모 회원가입'], ['03', '자녀 프로필'], ['04', '돈 습관 진단'], ['05', '진단 결과'], ['06', '목표 스노우볼']] },
    { id: 'child', label: '아이', icon: '😊', items: [['07', '아이 홈'], ['08', '용돈 기록'], ['09', '오늘의 미션']] },
    { id: 'parent', label: '부모', icon: '👨‍👩‍👧', items: [['10', '부모 홈'], ['11', '미션 승인'], ['12', '보상 설정'], ['13', '주간 리포트'], ['14', '가족 대화'], ['15', '부모 응원']] },
    { id: 'growth', label: '성장', icon: '📈', items: [['16', '배지와 레벨'], ['17', '30일 리포트'], ['18', '수료증']] }
  ];
  return (
    <AppFrame screenClass="operations-index-screen-1-9">
      <div className="operations-index operations-index-1-9">
        <section className="operations-hero-1-9">
          <div className="operations-hero-copy"><p className="release-pill">OPERATIONS MAP · RELEASE 2.13</p><h1>MY real Money<br /><strong>스노우볼 굴리기</strong></h1><p>아이의 금융 습관을 즐겁고 단단하게 키우는<br />통합 운영 스타터 화면입니다.</p></div>
          <Character src={haneul('wave')} alt="인사하는 하늘이" />
          <span className="operations-snow one">❄</span><span className="operations-snow two">✦</span>
        </section>

        <div className="operations-capability-row"><span>▣ 18개 화면</span><span>◇ 모듈형</span><span>▤ 반응형</span><span>⚑ Phase 1–3</span></div>
        <div className="index-actions operations-actions"><Link className="primary-button link-button" to="/demo">목적별 MRM 데모 <b>›</b></Link><Link className="secondary-button link-button" to="/login">운영 로그인 <b>›</b></Link><Link className="secondary-button link-button" to="/01">온보딩 보기 <b>›</b></Link></div>
        <RoleSwitcher />

        <section className="operations-role-grid">
          <Link to="/child/home"><div><strong>아이 경험</strong><span>목표·기록·미션·배지</span></div><Character src={haneul('heart')} alt="아이 경험" /><b>›</b></Link>
          <Link to="/parent/home"><div><strong>부모 경험</strong><span>승인·보상·리포트</span></div><Character src={sage('checklist')} alt="부모 경험" /><b>›</b></Link>
          <Link to="/admin"><div><strong>관리자 콘솔</strong><span>운영·콘텐츠·통계</span></div><Character src={haneul('report')} alt="관리자 콘솔" /><b>›</b></Link>
        </section>

        <div className="screen-shortcut-heading"><strong>☆ 18개 화면 바로가기</strong><span>카드를 눌러 이동하세요</span></div>
        <div className="screen-group-list">{groups.map((group) => <section className={`screen-group ${group.id}`} key={group.id}><div className="screen-group-label"><span>{group.icon}</span><strong>{group.label}</strong></div><div className="screen-link-grid">{group.items.map(([id, label]) => <Link key={id} to={`/${id}`}><strong>{id}</strong><span>{label}</span><b>›</b></Link>)}</div></section>)}</div>
        <Link className="admin-link operations-admin-banner" to="/admin"><Character src={haneul('wave')} alt="" /><strong>반응형 관리자 콘솔</strong><b>›</b></Link>
        <p className="operations-note">이 화면은 발표·검수용 기존 통합 런처입니다. 일반 사용자 첫 화면은 / 경로에서 단순하게 제공됩니다.</p>
      </div>
    </AppFrame>
  );
}

function Loading() {
  return <AppFrame><div className="loading-screen">데이터를 준비하고 있어요…</div></AppFrame>;
}
